import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * [MCV-1919] Whether the desktop sidebar is expanded (288px) or collapsed to its icon rail (56px),
 * and whether a `Layout` (the only owner of a sidebar) is on screen at all.
 *
 * It is a module-level store, read through useSyncExternalStore, for two reasons:
 *  - the choice has to survive page changes and reloads. Every authenticated page mounts its own
 *    `Layout`, so component state reset the sidebar to its default on each navigation. It is kept
 *    in localStorage, like the collapsed nav sections (SideNav/NavComponent).
 *  - the floating Feedback button lives OUTSIDE `Layout` (AuthenticatedRoutes mounts it, so it also
 *    shows on registration pages that have no sidebar) yet has to sit beside the sidebar's current
 *    width. A store is the one thing both can read.
 *
 * Every localStorage access is guarded: private mode and blocked storage throw, and the sidebar then
 * simply works for the session without persisting.
 */
export const SIDEBAR_OPEN_STORAGE_KEY = 'layout:sidebar:open';

const readStoredOpen = (): boolean => {
  try {
    // Only an explicit 'false' collapses; nothing stored (or anything unexpected) is open.
    return window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const subscribe = (onChange: () => void): (() => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

// Read once, then kept in step by setNavOpen and by the cross-tab `storage` event. Starting from
// storage (not from a constant) is what makes the choice survive a reload.
let navOpen = readStoredOpen();
let mountedLayouts = 0;

if (typeof window !== 'undefined') {
  // The native `storage` event only fires in OTHER tabs, never the one that wrote.
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && event.key !== SIDEBAR_OPEN_STORAGE_KEY) return;
    const next = readStoredOpen();
    if (next === navOpen) return;
    navOpen = next;
    notify();
  });
}

const setNavOpen = (open: boolean) => {
  if (open === navOpen) return;
  navOpen = open;
  try {
    window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(open));
  } catch {
    // Private mode / quota: the sidebar still toggles for this session.
  }
  notify();
};

const getNavOpen = () => navOpen;
const getMounted = () => mountedLayouts > 0;

/** The sidebar's open state and its setter. Used by `Layout`, which owns the toggle. */
export const useSidebarNavOpen = (): [boolean, (open: boolean) => void] => {
  const open = useSyncExternalStore(subscribe, getNavOpen, getNavOpen);
  return [open, setNavOpen];
};

/**
 * What a component outside `Layout` needs to clear the sidebar: `mounted` is false on pages with no
 * Layout (registration), where there is nothing to clear, and `open` is the sidebar's current width.
 */
export const useDockedSidebar = (): { mounted: boolean; open: boolean } => {
  const mounted = useSyncExternalStore(subscribe, getMounted, getMounted);
  const open = useSyncExternalStore(subscribe, getNavOpen, getNavOpen);
  return { mounted, open };
};

/**
 * Called by `Layout`: counts it as mounted for as long as it is on screen. A layout effect, so the
 * Feedback button moves aside before the first paint instead of jumping after it.
 */
export const useRegisterMountedLayout = (): void => {
  useLayoutEffect(() => {
    mountedLayouts += 1;
    notify();
    return () => {
      mountedLayouts -= 1;
      notify();
    };
  }, []);
};
